import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import {
  Box,
  Chip,
  Fab,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { type FC, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import ActivityCaptureGallery from './ActivityCaptureGallery.js';
import ActivityDimensionChips from './ActivityDimensionChips.js';
import ActivityRejectDialog from './ActivityRejectDialog.js';
import {
  type ActivityEvent,
  ActivityApi,
  useActivityEvents,
} from '../../api/index.js';
import { activityEventRemoved } from '../../redux/reducers/pendingActivityEvents.js';

type PatientParams = { patientId: string };

const REFRESH_INTERVAL_MS = 60_000;
const WINDOW_MINUTES = 60;

export const ActivityDebugWidget: FC = () => {
  const { patientId = '' } = useParams<PatientParams>();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<ActivityEvent | null>(null);

  const window = useMemo(() => ({
    from: DateTime.now().minus({ minutes: WINDOW_MINUTES }).toISO() ?? '',
    to: DateTime.now().toISO() ?? '',
  }), [
    // Recompute every refresh tick so the rolling window slides forward.
    // The query key includes `from`/`to`, so a new key fires a fresh fetch.
    Math.floor(Date.now() / REFRESH_INTERVAL_MS),
  ]);

  const query = useActivityEvents(
    { patientId, from: window.from, to: window.to, limit: 50 },
    {
      enabled: Boolean(patientId),
      refetchInterval: REFRESH_INTERVAL_MS,
      refetchIntervalInBackground: true,
    },
  );

  if (!patientId) {
    return null;
  }

  const items: ActivityEvent[] = query.data?.pages.flatMap((p) => p.items) ?? [];

  const onAccept = async (event: ActivityEvent) => {
    dispatch(activityEventRemoved(event.id));
    await ActivityApi.reviewEvent(event.id, { verdict: 'accepted' });
    await query.refetch();
  };

  const onReject = (event: ActivityEvent) => {
    setRejecting(event);
  };

  if (!expanded) {
    return (
      <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1300 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <Fab
            size="medium"
            color="primary"
            onClick={() => setExpanded(true)}
            aria-label="Open AI activity review panel"
          >
            <AutoAwesomeIcon />
          </Fab>
          {items.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -2,
                right: -2,
                minWidth: 20,
                height: 20,
                px: 0.75,
                borderRadius: '10px',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: 11,
                fontWeight: 700,
                lineHeight: '20px',
                textAlign: 'center',
                boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
                // MUI's `Fab` hard-codes `zIndex: theme.zIndex.fab` (= 1050)
                // on its styled root, so the badge has to outrank it.
                zIndex: (theme) => theme.zIndex.fab + 1,
                pointerEvents: 'none',
              }}
            >
              {items.length > 99 ? '99+' : items.length}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1300,
        width: 380,
        maxHeight: 480,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <AutoAwesomeIcon fontSize="small" color="primary" sx={{ mr: 0.75 }} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          AI Activity — last hour
        </Typography>
        <IconButton size="small" onClick={() => query.refetch()} aria-label="Refresh">
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => setExpanded(false)} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {items.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {query.isLoading ? 'Loading…' : 'No events in the last hour.'}
            </Typography>
          </Box>
        ) : (
          items.map((event) => (
            <ActivityRow
              key={event.id}
              event={event}
              onOpenGallery={() => setPlayingId(event.id)}
              onAccept={() => onAccept(event)}
              onReject={() => onReject(event)}
            />
          ))
        )}
      </Box>
      {rejecting && (
        <ActivityRejectDialog
          event={rejecting}
          onClose={() => setRejecting(null)}
          onReviewed={(reviewed) => {
            dispatch(activityEventRemoved(reviewed.id));
            setRejecting(null);
            void query.refetch();
          }}
        />
      )}
      <ActivityCaptureGallery eventId={playingId} onClose={() => setPlayingId(null)} />
    </Paper>
  );
};

interface ActivityRowProps {
  event: ActivityEvent;
  onOpenGallery: () => void;
  onAccept: () => void;
  onReject: () => void;
}

const ActivityRow: FC<ActivityRowProps> = ({
  event,
  onOpenGallery,
  onAccept,
  onReject,
}) => {
  const time = DateTime.fromISO(event.capturedAt).toFormat('HH:mm:ss');
  const captureCount = event.capturesCount ?? 0;
  const playable = captureCount > 0 || Boolean(event.videoS3Key);
  const accent = event.code.category?.color ?? 'transparent';
  const reviewStatus = event.reviewStatus ?? null;
  const correctedCode = event.reviewLog?.code.code ?? null;

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        borderLeft: 3,
        borderLeftColor: accent,
        transition: 'background-color 120ms',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Typography
          variant="caption"
          sx={{ minWidth: 56, fontFamily: 'monospace', color: 'text.secondary', pt: 0.25 }}
        >
          {time}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            title={event.code.description}
            sx={{ fontWeight: 600, lineHeight: 1.3 }}
          >
            <span style={{ opacity: 0.6, marginRight: 4 }}>{event.code.code}</span>
            {event.code.description}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: 'block', lineHeight: 1.4 }}
          >
            {event.camera.name}
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center' }}
          >
            <Chip
              size="small"
              variant="outlined"
              label={`${Math.round(event.confidence * 100)}%`}
            />
            {reviewStatus === 'accepted' ? (
              <Chip size="small" variant="outlined" color="success" label="Accepted" />
            ) : reviewStatus === 'rejected' ? (
              <Chip
                size="small"
                variant="outlined"
                color="error"
                label={`Rejected → ${correctedCode ?? ''}`}
              />
            ) : null}
            {event.dimensions && <ActivityDimensionChips dimensions={event.dimensions} />}
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.25} sx={{ mt: 0.25 }}>
          {playable ? (
            <Tooltip title={captureCount > 0 ? `View all ${captureCount} snippets` : 'View clip'}>
              <IconButton size="small" onClick={onOpenGallery} aria-label="View snippets">
                <VideoLibraryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title={reviewStatus ? 'Change to accepted' : 'Accept'}>
            <IconButton size="small" color="success" onClick={onAccept} aria-label="Accept">
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={reviewStatus ? 'Change — pick a different code' : 'Reject — pick a different code'}>
            <IconButton size="small" color="error" onClick={onReject} aria-label="Reject">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ActivityDebugWidget;
